import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import { NavBar, NavBarButton } from '../../components/NavBar'
import { sendSubscribeAgent, sendUnsubscribeAgent } from '../../api/ws'
import { bridge } from '../../bridge'
import { TranscriptSegment } from './segments/TranscriptSegment'
import { HistorySegment } from './segments/HistorySegment'
import './AgentDetailScreen.css'

type Segment = 'transcript' | 'history'

export function AgentDetailScreen({
  agentInstanceID,
  positionID,
}: {
  agentInstanceID: string
  positionID: string
}) {
  const [segment, setSegment] = useState<Segment>('transcript')
  const popNav = useAppStore((s) => s.popNav)
  const pushNav = useAppStore((s) => s.pushNav)
  const agent = useAppStore((s) =>
    s.agentInstances.find((a) => a.id === agentInstanceID),
  )
  const hostname = useAppStore((s) => {
    const host = agent ? s.hosts.find((h) => h.host_id === agent.host_id) : undefined
    return host?.hostname ?? ''
  })
  const setSelected = useAppStore((s) => s.setSelectedAgentInstance)

  const isActive = agent?.status === 'active'
  const pidStatus = agent?.pid_status ?? ''

  useEffect(() => {
    setSelected(agentInstanceID)
    return () => setSelected(null)
  }, [agentInstanceID, setSelected])

  useEffect(() => {
    sendSubscribeAgent(agentInstanceID)
    return () => sendUnsubscribeAgent(agentInstanceID)
  }, [agentInstanceID])

  // Opening the transcript acknowledges the agent's notifications (@mentions
  // route here). Drop its delivered OS banners — the tapped one and siblings.
  // Best-effort; no-op without a native tray, tolerant of older native shells.
  useEffect(() => {
    void bridge
      .call('notifications.clearForTarget', { aiAgentInstanceID: agentInstanceID })
      .catch(() => {})
  }, [agentInstanceID])

  return (
    <div className="agent-detail">
      <NavBar
        title={agent?.name ?? 'Agent'}
        leading={<NavBarButton icon="‹" label="Back" onClick={popNav} />}
        trailing={
          <NavBarButton
            icon="⚙︎"
            label="Settings"
            onClick={() =>
              pushNav({
                kind: 'agent_settings',
                agentInstanceID,
                positionID,
                agentName: agent?.name ?? 'Agent',
                hostname,
              })
            }
          />
        }
      />

      <div className="agent-detail__segmented">
        <div className="segmented" role="tablist">
          {(['transcript', 'history'] as Segment[]).map((s) => (
            <button
              key={s}
              role="tab"
              aria-selected={segment === s}
              className={`segmented__item${segment === s ? ' segmented__item--active' : ''}`}
              onClick={() => setSegment(s)}
            >
              {s === 'transcript' ? 'Chat' : 'Approval History'}
            </button>
          ))}
        </div>
      </div>

      <div className="agent-detail__body">
        {segment === 'transcript' && (
          <TranscriptSegment
            agentInstanceID={agentInstanceID}
            isActive={isActive}
            pidStatus={pidStatus}
          />
        )}
        {segment === 'history' && <HistorySegment positionID={positionID} />}
      </div>
    </div>
  )
}
